/**
 * Hook for fetching weekly calendar data
 * Provides data for the 7-day calendar grid view
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { keepPreviousDataForOrg } from '@/lib/tenantPlaceholder';
import { useLingui } from '@lingui/react';
import type { WeekViewModel, WeeklyGroupView, CalendarCellView, CalendarChildView, WeekCalendarData, DayGroupCalendar } from '@/types/calendar';
import { OpenAPI } from '@/api';
import { request as __request } from '@/api/core/request';
import type { capacity_WeekCalendarResponse } from '@/api';
import { formatDateLocalYYYYMMDD, getWeekStartsOn } from '@/utils/localeUtils';

/**
 * Maps generated API type to domain type at the API boundary.
 * This isolates the rest of the application from generated code changes.
 */
function mapApiResponseToDomain(apiResponse: capacity_WeekCalendarResponse): WeekCalendarData {
  return {
    week_start: apiResponse.week_start ?? '',
    week_end: apiResponse.week_end ?? '',
    days: (apiResponse.days ?? []).map(day => ({
      date: day.date ?? '',
      day_of_week: day.day_of_week ?? '',
      groups: (day.groups ?? []).map(group => ({
        group_id: group.group_id ?? '',
        group_name: group.group_name ?? '',
        group_type: group.group_type,
        date: group.date ?? '',
        allocations: (group.allocations ?? []).map(alloc => ({
          allocation_id: alloc.allocation_id ?? '',
          child_id: alloc.child_id ?? '',
          child_firstname: alloc.child_firstname ?? '',
          child_lastname: alloc.child_lastname ?? '',
          childInternalId: alloc.childInternalId,
          avatarUrl: alloc.avatarUrl,
          age_type_code: alloc.age_type_code ?? 'CHILD',
          services: alloc.services ?? [],
          is_absent: alloc.is_absent ?? false,
          has_override: alloc.has_override ?? false,
          override_group_id: alloc.override_group_id,
          is_extra_daycare: alloc.is_extra_daycare ?? false,
          updated_at: alloc.updated_at ?? '',
          absence_reason: alloc.absence_reason,
          absence_is_full_day: alloc.absence_is_full_day,
          birthdate: alloc.birthdate,
          ages_out_on: alloc.ages_out_on,
          allocation_from_date: alloc.allocation_from_date,
          allocation_to_date: alloc.allocation_to_date,
          is_joining_this_day: alloc.is_joining_this_day,
          is_leaving_this_day: alloc.is_leaving_this_day,
        })),
        utilization: {
          group_id: group.utilization?.group_id ?? '',
          date: group.utilization?.date ?? '',
          headcount_capacity: group.utilization?.max_headcount ?? 0,
          headcount_allocated: group.utilization?.headcount ?? 0,
          headcount_utilization_pct: group.utilization?.headcount_utilization_percent ?? null,
          weighted_capacity: group.utilization?.max_weighted_headcount ?? 0,
          weighted_allocated: group.utilization?.weighted_headcount ?? 0,
          weighted_utilization_pct: group.utilization?.weighted_utilization_percent ?? null,
          status: group.utilization?.status ?? '',
          has_service_breakdown: group.utilization?.has_service_breakdown,
          peak_service: group.utilization?.peak_service,
          peak_utilization: group.utilization?.peak_utilization,
        },
        services: group.services,
        joining_count: group.joining_count,
        leaving_count: group.leaving_count,
        avatar_url: group.avatar_url,
        icon: group.icon,
        color: group.color,
        childcare_id: group.childcare_id,
        age_min: group.age_min,
        age_max: group.age_max,
      })),
    })),
    service_time_ranges: (apiResponse.service_time_ranges ?? []).map(range => ({
      service_code: range.service_code ?? '',
      start_time: range.start_time ?? '',
      end_time: range.end_time ?? '',
    })),
    group_transitions: (apiResponse.group_transitions ?? []).map(gt => ({
      group_id: gt.group_id ?? '',
      joining_count: gt.joining_count ?? 0,
      leaving_count: gt.leaving_count ?? 0,
    })),
  };
}

/**
 * Transform domain calendar data (days→groups structure) to UI view-model (groups→days structure).
 * Pivots the data and maps allocations to CalendarChildView for UI consumption.
 */
function transformWeekCalendarData(domainData: WeekCalendarData): WeekViewModel {
  // Build a map of groupId → group data
  const groupsMap = new Map<string, WeeklyGroupView>();

  // Iterate over days to collect group information
  domainData.days.forEach((day) => {
    day.groups.forEach((dayGroup: DayGroupCalendar) => {
      const groupId = dayGroup.group_id;
      const groupName = dayGroup.group_name;
      const groupType = dayGroup.group_type;

      if (!groupsMap.has(groupId)) {
        groupsMap.set(groupId, {
          id: groupId,
          name: groupName,
          type: groupType,
          avatarUrl: dayGroup.avatar_url,
          icon: dayGroup.icon,
          color: dayGroup.color,
          childcareId: dayGroup.childcare_id,
          ageMin: dayGroup.age_min,
          ageMax: dayGroup.age_max,
          days: [],
        });
      }

      // Map allocations to children
      const children: CalendarChildView[] = dayGroup.allocations.map((alloc) => ({
        id: alloc.child_id,
        name: `${alloc.child_firstname} ${alloc.child_lastname}`.trim(),
        services: alloc.services,
        allocationId: alloc.allocation_id,
        ageTypeCode: alloc.age_type_code,
        avatarUrl: alloc.avatarUrl,
        childInternalId: alloc.childInternalId,
        isAbsent: alloc.is_absent,
        hasOverride: alloc.has_override,
        overrideGroupId: alloc.override_group_id,
        isExtraDaycare: alloc.is_extra_daycare,
        absenceReason: alloc.absence_reason,
        absenceIsFullDay: alloc.absence_is_full_day,
        birthdate: alloc.birthdate,
        agesOutOn: alloc.ages_out_on,
        allocationFromDate: alloc.allocation_from_date,
        allocationToDate: alloc.allocation_to_date,
        isJoiningThisDay: alloc.is_joining_this_day,
        isLeavingThisDay: alloc.is_leaving_this_day,
      }));

      // Create cell view with utilization data
      const utilization = dayGroup.utilization;
      const cellView: CalendarCellView = {
        groupId: groupId,
        date: dayGroup.date,
        childCount: utilization.headcount_allocated,
        capacity: utilization.headcount_capacity,
        utilization: utilization.headcount_utilization_pct ?? 0, // Default to 0 when capacity not configured
        weightedCount: utilization.weighted_allocated,
        weightedCapacity: utilization.weighted_capacity,
        weightedUtilization: utilization.weighted_utilization_pct ?? 0, // Default to 0 when capacity not configured
        children,
        hasServiceBreakdown: utilization.has_service_breakdown,
        peakService: utilization.peak_service,
        peakUtilization: utilization.peak_utilization,
        services: dayGroup.services,
        joiningCount: dayGroup.joining_count,
        leavingCount: dayGroup.leaving_count,
      };

      groupsMap.get(groupId)!.days.push(cellView);
    });
  });

  // Build group transition lookup from API response
  const transitionMap = new Map<string, { joining: number; leaving: number }>();
  if (domainData.group_transitions) {
    for (const gt of domainData.group_transitions) {
      transitionMap.set(gt.group_id, { joining: gt.joining_count, leaving: gt.leaving_count });
    }
  }

  // Convert map to array, sort days by date, and add weekly transition totals
  const groups = Array.from(groupsMap.values()).map((group) => {
    const transition = transitionMap.get(group.id);
    return {
      ...group,
      days: group.days.sort((a, b) => a.date.localeCompare(b.date)),
      weekJoiningCount: transition?.joining ?? 0,
      weekLeavingCount: transition?.leaving ?? 0,
    };
  });

  return {
    week_start: domainData.week_start,
    week_end: domainData.week_end,
    groups,
    serviceTimeRanges: domainData.service_time_ranges,
  };
}

// Query key factory for weekly calendar data
export const calendarKeys = {
  all: ['calendar'] as const,
  // Keys carry orgId at index 2 — the slot keepPreviousDataForOrg reads to keep
  // the placeholder tenant-scoped — and every arg the
  // query actually varies by — including weekStartsOn, which the request sends:
  // without it a locale switch changed the request but reused the cached week.
  // The date is formatted locally; toISOString() would encode a UTC instant for
  // a local-midnight Date.
  week: (orgId: string, weekStart: Date, weekStartsOn: number) =>
    [...calendarKeys.all, 'week', orgId, formatDateLocalYYYYMMDD(weekStart), { weekStartsOn }] as const,
  quarter: (orgId: string, year: number, quarter: number) =>
    [...calendarKeys.all, 'quarter', orgId, { year, quarter }] as const,
};

/**
 * Fetches weekly calendar data for a given week start date
 * @param weekStart - The start of the week to fetch data for (locale-aware)
 * @param options - `enabled` (default `true`) lets a caller hold the request
 *   back until it is actually needed — the plan-group-changes worklist only
 *   wants the week payload in its "All children" mode. The query KEY is
 *   unchanged either way, so a gated caller still shares the cache entry.
 * @returns Query result with weekly calendar data
 */
export function useCalendarWeek(weekStart: Date, options?: { enabled?: boolean }) {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();
  const { i18n } = useLingui();
  const weekStartsOn = getWeekStartsOn(i18n.locale);

  return useQuery({
    queryKey: calendarKeys.week(orgId || '', weekStart, weekStartsOn),
    queryFn: async (): Promise<WeekViewModel> => {
      // Use local date formatting to avoid timezone drift
      const startDateStr = formatDateLocalYYYYMMDD(weekStart);

      // Call the API with new parameters (start_date + week_starts_on)
      const apiResponse = await __request<capacity_WeekCalendarResponse>(OpenAPI, {
        method: 'GET',
        url: '/api/v1/capacity/calendar/week',
        query: {
          'start_date': startDateStr,
          'week_starts_on': weekStartsOn,
          // Send legacy week_start for backward compatibility during rollout
          'week_start': startDateStr,
        },
        errors: {
          400: `Invalid parameters`,
          401: `Authentication required or invalid credentials`,
          403: `Organization ID not found in token`,
          500: `Failed to fetch calendar data`,
        },
      });

      // Map generated API type to domain type at the boundary
      const domainData = mapApiResponseToDomain(apiResponse);

      // Transform domain data to UI view-model
      return transformWeekCalendarData(domainData);
    },
    // Keep the previous week on screen while the next one loads — only within
    // the same tenant; an org switch resolves to a real loading state.
    placeholderData: keepPreviousDataForOrg<WeekViewModel>(orgId),
    enabled: isSignedIn && isAuthReady && !!orgId && (options?.enabled ?? true),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401/403/404 errors
      if (error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('404')
      )) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
